import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  FiberManualRecord as FiberManualRecordIcon
} from '@material-ui/icons';
import InlineTextPopover from 'components/@common/InlineTextPopover';
import Maybe from 'components/@common/Maybe';
import { ProductCurriculum } from 'types/schema-generated-types';
import { isPrmSetACurriculum } from '../curriculum-util';
import LargeProgressBar from './LargeProgressBar';
import { calcPercent } from './util';

type Props = {
  productCurriculum?: ProductCurriculum | null;
  completedPercent: any;
  supportedPercent: any;
};

const colors = {
  primary: {
    met: '#51A486',
    notMet: '#EA031F'
  },
  secondary: {
    met: '#FFC20E',
    notMet: '#919191'
  }
};

const LargeBarChart: React.FC<Props> = ({
  productCurriculum,
  completedPercent,
  supportedPercent
}) => {
  const classes = useStyles();
  const { t } = useTranslation('ReportsHelpCommon');

  const completedTitleText = 'Completed Lessons';
  const completedTitle = isPrmSetACurriculum(productCurriculum) ? (
    <InlineTextPopover
      PopoverContentProps={{ description: t('prmSetAReporting') }}
      style={DEFAULT_TITLE_STYLE}
    >
      {completedTitleText}
    </InlineTextPopover>
  ) : (
    <Title>{completedTitleText}</Title>
  );

  return (
    <Box display="flex" flex={1} flexDirection="column">
      <LargeProgressBar
        currentPercent={Math.round(completedPercent)}
        metColor={colors.primary.met}
        notMetColor={colors.primary.notMet}
        metDescription={<Description text="Skills Met" />}
        MetEndAdornment={
          <CheckCircleIcon
            fontSize="small"
            color="inherit"
            classes={{ root: `${classes.icon} ${classes.check}` }}
          />
        }
        notMetDescription={<Description text="Skills Not Met" />}
        NotMetEndAdornment={
          <CancelIcon fontSize="small" color="error" classes={{ root: classes.icon }} />
        }
        title={completedTitle}
      />
      <Maybe shouldRender={completedPercent > 0}>
        <Box width={`${calcPercent(completedPercent, 100)}%`}>
          <LargeProgressBar
            title={<Title>Supports Provided</Title>}
            notMetDescription={
              <Description text="Did not need Support Path" style={{ marginLeft: 6 }} />
            }
            currentPercent={Math.round(supportedPercent)}
            metColor={colors.secondary.met}
            notMetColor={colors.secondary.notMet}
            metDescription={<Description text="Needed Support Path" />}
            MetEndAdornment={
              <FiberManualRecordIcon
                style={{
                  fontSize: 12,
                  alignSelf: 'center',
                  color: '#FFC20E'
                }}
              />
            }
          />
        </Box>
      </Maybe>
    </Box>
  );
};

const DEFAULT_TITLE_STYLE = { fontSize: 24 };

const Title = ({
  children,
  style = DEFAULT_TITLE_STYLE
}: {
  children: string;
  style?: React.CSSProperties;
}) => <Typography style={style}>{children}</Typography>;

const Description = ({ text, style }: { text: string; style?: React.CSSProperties }) => (
  <Typography variant="h6" style={{ fontWeight: 400, ...style }}>
    {text}
  </Typography>
);

const useStyles = makeStyles(() => ({
  icon: {
    fontSize: 12,
    alignSelf: 'center',
    marginLeft: 4
  },
  check: {
    color: '#51A486'
  }
}));

export default LargeBarChart;
